import { Component, OnDestroy, inject } from '@angular/core';
import { omit } from 'lodash';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { ProjectEnvEditForm } from '@zerops/zui/project-env-edit-form';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { formValueOnValid } from '@zerops/zef/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { FEATURE_NAME } from './project-env-edit-dialog.constant';
import { ProjectEnvEditDialogTranslations } from './project-env-edit-dialog.translations';
import { updateProjectEnv, revealProjectEnv } from '@zerops/zerops/core/project-base/project-base.action';
import { selectRevealedProjectEnvContent } from '@zerops/zerops/core/project-base';
import { ProjectEnvTypes } from '@zerops/models/project';

@Component({
  selector: 'z-project-env-edit-dialog',
  templateUrl: './project-env-edit-dialog.container.html',
  styleUrls: [ './project-env-edit-dialog.container.scss' ]
})
export class ProjectEnvEditDialogContainer extends ZefReactiveComponent implements OnDestroy {

  /**
   * Dependency injections.
   */
  #store = inject(Store);
  #projectEnvEditForm = inject(ProjectEnvEditForm);
  projectEnvTypes = ProjectEnvTypes;

  /**
   * Event streams properties.
   */
  onUpdate$ = new Subject<void>();
  onClose$ = new Subject<void>();
  onReveal$ = new Subject<void>();

  /**
   * Forms properties.
   */
  formState$ = this.#projectEnvEditForm.state$;

  /**
   * Sync properties.
   */
  updateKey = updateProjectEnv.type;
  revealKey = revealProjectEnv.type;
  dialogKey = FEATURE_NAME;

  /**
   * Async properties.
   */
  dialogState$ = this.#store.pipe(
    select(selectZefDialogState(FEATURE_NAME))
  );
  projectEnvEditDialogItemTranslations$ = this.translate$<ProjectEnvEditDialogTranslations>(FEATURE_NAME);
  revealedContent$ = this.#store.pipe(select(selectRevealedProjectEnvContent));
  formValue$ = this.formState$.pipe(map((state) => state.value));

  // A secret opens the editor with an empty content field, so changing it costs no
  // sudo at all. The decrypted value is pulled in only when the user explicitly asks
  // for it; the trigger disappears once that value is actually in the field (either
  // through this button or because the list had already revealed it).
  canReveal$ = combineLatest([ this.formValue$, this.revealedContent$ ]).pipe(
    map(([ value, revealed ]) => !!value.sensitive && revealed[value.id] === undefined)
  );

  /**
   * State resolver property.
   */
  state = this.$connect({
    open: this.dialogState$.pipe(map((d) => !!d.state)),
    formState: this.formState$,
    projectEnvEditDialogItemTranslations: this.projectEnvEditDialogItemTranslations$,
    canReveal: this.canReveal$
  });

  /**
   * Action streams methods.
   */
  private _updateAction$ = this.onUpdate$.pipe(
    formValueOnValid(this.#projectEnvEditForm),
    map((formValue) => {
      const { id } = formValue;
      return updateProjectEnv({
        data: {
          id,
          payload: omit(formValue, [ 'id', 'type' ])
        },
        meta: { source: FEATURE_NAME, type: formValue.type }
      }, { type: 'pop' });
    })
  );

  private _closeAction$ = this.onClose$.pipe(
    map(() => zefDialogClose({ key: FEATURE_NAME }))
  );

  private _revealAction$ = this.onReveal$.pipe(
    withLatestFrom(this.formValue$),
    map(([ _, { id } ]) => revealProjectEnv({ id }))
  );

  // Fill the content field once the reveal lands, which may be a whole sudo re-auth
  // round trip later - the sudo flow replays the reveal action, so waiting on the
  // revealed content itself is what survives it.
  private _fillRevealedAction$ = this.onReveal$.pipe(
    withLatestFrom(this.formValue$),
    switchMap(([ _, { id } ]) => this.revealedContent$.pipe(
      map((revealed) => revealed[id]),
      filter((content) => content !== undefined),
      take(1),
      map((content) => this.#projectEnvEditForm.setValue('content', content))
    ))
  );

  constructor() {
    super();

    this.$dispatchActions([
      this._updateAction$,
      this._closeAction$,
      this._revealAction$,
      this._fillRevealedAction$
    ]);
  }

  // TODO https://github.com/typebytes/ngx-template-streams/issues/8
  ngOnDestroy() {
    super.ngOnDestroy();
  }

}
